import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { PROJECTS, STATS, PRIMARY_CTA, IMAGES } from "@/lib/data";

const Projects = () => {
  const totalUnits = PROJECTS.reduce((acc, p) => acc + p.units, 0);
  const maxUnits = PROJECTS.reduce((acc, p) => Math.max(acc, p.units), 0);

  return (
    <main data-testid="page-projects">
      <PageHero
        eyebrow="Projects"
        title="Federally regulated relocations across DC, Maryland & Virginia."
        intro="Up to 240 units per project and more than 1,500 households in total."
      />

      {/* Snapshot stats */}
      <section className="bg-[#F8F9FA] border-b border-gray-200" data-testid="projects-snapshot">
        <div className="container-kc py-12 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200">
            {[
              { value: PROJECTS.length.toString(), label: "Projects Featured" },
              { value: totalUnits.toLocaleString() + "+", label: "Units Across Projects" },
              { value: maxUnits.toString(), label: "Largest Single Project" },
              { value: STATS[3].value, label: "Service Region" },
            ].map((s, idx) => (
              <Reveal key={s.label} delay={idx * 0.05}>
                <div className="bg-[#F8F9FA] p-6 md:p-8 h-full">
                  <div className="font-serif text-3xl md:text-4xl text-[#000000]">{s.value}</div>
                  <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-gray-500">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Banner image */}
      <section className="bg-white" data-testid="projects-banner">
        <div className="container-kc py-16 md:py-20">
          <Reveal>
            <div className="aspect-[21/9] overflow-hidden bg-gray-100">
              <img src={IMAGES.projectsBanner} alt="Affordable housing community in Washington, DC" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Project table */}
      <section className="section bg-white" data-testid="projects-table">
        <div className="container-kc">
          <Reveal>
            <div className="flex items-center gap-3 mb-10">
              <span className="hairline--gold" />
              <span className="eyebrow">Engagement Roster</span>
            </div>
          </Reveal>

          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full border-collapse" data-testid="projects-desktop-table">
              <thead>
                <tr className="border-t border-b border-[#000000]">
                  <th className="text-left py-4 pr-6 text-[11px] tracking-[0.2em] uppercase text-[#000000] font-semibold w-[60%]">Project</th>
                  <th className="text-left py-4 text-[11px] tracking-[0.2em] uppercase text-[#000000] font-semibold">Location</th>
                </tr>
              </thead>
              <tbody>
                {PROJECTS.map((p, idx) => (
                  <tr
                    key={p.name}
                    data-testid={`project-row-${idx}`}
                    className="border-b border-gray-200"
                  >
                    <td className="py-6 pr-6 align-top">
                      <div className="font-serif text-xl text-[#000000]">{p.name}</div>
                    </td>
                    <td className="py-6 align-top text-[13px] text-gray-600 uppercase tracking-[0.08em]">{p.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-6">
            {PROJECTS.map((p, idx) => (
              <Reveal key={p.name} delay={idx * 0.03}>
                <div className="border-t border-[#000000] pt-6" data-testid={`project-card-${idx}`}>
                  <h3 className="font-serif text-2xl text-[#000000] leading-snug">{p.name}</h3>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 mt-2">{p.location}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#000000] text-white">
        <div className="container-kc py-24 md:py-32">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <div className="eyebrow eyebrow--gold mb-4">Have a Project?</div>
                <h2 className="h-display h-display--inverse text-3xl md:text-5xl">
                  Bring your next relocation to a partner who has delivered.
                </h2>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link to={PRIMARY_CTA.to} className="btn btn--inverse" data-testid="projects-cta">
                  {PRIMARY_CTA.label} <span className="btn-arrow">→</span>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Projects;
